import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Smartphone, Apple, X, Loader2, ExternalLink, Copy, Check, QrCode, Monitor } from 'lucide-react';
import { toast } from 'sonner';
import { trackAppExport, trackFeatureUsed } from '@/lib/posthog';

// App Store URLs
const APP_STORE_IOS = "https://apps.apple.com/nl/app/edomatch/id6498934535?l=en-GB";
const APP_STORE_ANDROID = "https://play.google.com/store/apps/details?id=com.edogroup.edomatch&pcampaignid=web_share";

/**
 * Detect if the user is on a mobile device
 */
const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Check for mobile user agents
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i;
  
  // Also check screen width as fallback
  const isSmallScreen = window.innerWidth <= 768;
  
  return mobileRegex.test(userAgent.toLowerCase()) || isSmallScreen;
};

/**
 * Simple QR Code component using a public API
 */
const QRCodeDisplay = ({ value, size = 180 }) => {
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}&format=svg`;
  
  return (
    <div className="flex justify-center p-4 bg-white rounded-xl border-2 border-gray-100">
      <img 
        src={qrUrl} 
        alt="QR Code" 
        className="w-[180px] h-[180px]"
        loading="eager"
      />
    </div>
  );
};

/**
 * OpenAppButton - Consistent button for opening EdoMatch mobile app with deep link
 * 
 * Features:
 * - Mobile: Attempts deep link directly, shows fallback modal if app not installed
 * - Desktop: Shows QR code and copy link options
 * - Environment-agnostic: Works even with minimal profile data
 * 
 * @param {string} profileId - The profile ID to export
 * @param {string} variant - Button style variant: "default" | "header" | "compact"
 * @param {string} className - Additional CSS classes
 * @param {function} onExportStart - Callback when export starts
 * @param {function} onExportComplete - Callback when export completes
 */
export default function OpenAppButton({ profileId, variant = "default", onExportStart, onExportComplete, className = "" }) {
  const { language } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  // Detect mobile on mount
  useEffect(() => {
    setIsMobile(isMobileDevice());
    
    // Also listen for resize
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const translations = {
    fr: {
      openApp: "Ouvrir dans EdoMatch",
      openAppShort: "EdoMatch",
      opening: "Préparation...",
      // Mobile modal
      mobileTitle: "Ouvrir dans l'app",
      mobileDesc: "Votre CV est prêt ! Utilisez-le directement dans l'app EdoMatch pour postuler aux offres.",
      openInApp: "Ouvrir dans l'app",
      // Desktop modal
      desktopTitle: "Ouvrir sur mobile",
      desktopDesc: "Scannez ce QR code avec votre téléphone pour ouvrir votre CV dans l'app EdoMatch.",
      scanQR: "Scanner le QR code",
      orCopyLink: "Ou copiez le lien",
      copyLink: "Copier le lien",
      copied: "Copié !",
      linkCopied: "Lien copié dans le presse-papiers",
      // Common
      getApp: "Télécharger l'app",
      appStore: "App Store",
      playStore: "Play Store",
      orGetApp: "Vous n'avez pas l'app ?",
      close: "Fermer",
      // Errors
      errorTitle: "Erreur",
      errorProfileNotFound: "Profil introuvable. Créez d'abord votre CV.",
      errorGeneric: "Une erreur est survenue. Réessayez."
    },
    en: {
      openApp: "Open in EdoMatch",
      openAppShort: "EdoMatch",
      opening: "Preparing...",
      // Mobile modal
      mobileTitle: "Open in App",
      mobileDesc: "Your CV is ready! Use it directly in the EdoMatch app to apply for jobs.",
      openInApp: "Open in App",
      // Desktop modal
      desktopTitle: "Open on Mobile",
      desktopDesc: "Scan this QR code with your phone to open your CV in the EdoMatch app.",
      scanQR: "Scan QR Code",
      orCopyLink: "Or copy the link",
      copyLink: "Copy Link",
      copied: "Copied!",
      linkCopied: "Link copied to clipboard",
      // Common
      getApp: "Get the App",
      appStore: "App Store",
      playStore: "Play Store",
      orGetApp: "Don't have the app?",
      close: "Close",
      // Errors
      errorTitle: "Error",
      errorProfileNotFound: "Profile not found. Create your CV first.",
      errorGeneric: "An error occurred. Please try again."
    }
  };

  const t = translations[language] || translations.en;

  const handleOpenApp = async () => {
    if (!profileId) {
      console.error('No profile ID provided');
      toast.error(t.errorProfileNotFound);
      return;
    }

    setIsLoading(true);
    setError(null);
    if (onExportStart) onExportStart();

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/cv/export-for-app`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          profile_id: profileId,
          language: language
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorCode = errorData?.detail?.error_code || 'UNKNOWN';
        
        if (errorCode === 'PROFILE_NOT_FOUND') {
          setError(t.errorProfileNotFound);
          toast.error(t.errorProfileNotFound);
        } else {
          setError(t.errorGeneric);
          toast.error(t.errorGeneric);
        }
        setIsLoading(false);
        return;
      }

      const data = await response.json();
      setExportData(data);
      
      // Track app export in PostHog
      trackAppExport(profileId);

      if (isMobile) {
        // On mobile: Try deep link directly
        window.location.href = data.deep_link;
        
        // After 2 seconds, show fallback modal (app might not be installed)
        setTimeout(() => {
          setShowModal(true);
          setIsLoading(false);
        }, 2000);
      } else {
        // On desktop: Show modal with QR code immediately
        setShowModal(true);
        setIsLoading(false);
      }

      if (onExportComplete) onExportComplete(data);

    } catch (error) {
      console.error('Export error:', error);
      setError(t.errorGeneric);
      toast.error(t.errorGeneric);
      setIsLoading(false);
    }
  };

  const handleOpenDeepLink = () => {
    if (exportData?.deep_link) {
      window.location.href = exportData.deep_link;
    }
  };

  const handleCopyLink = async () => {
    if (!exportData?.deep_link) return;
    
    try {
      await navigator.clipboard.writeText(exportData.deep_link);
      setCopied(true);
      toast.success(t.linkCopied);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = exportData.deep_link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      toast.success(t.linkCopied);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setCopied(false);
  };

  // Button styles based on variant
  const getButtonStyles = () => {
    switch (variant) {
      case "header":
        return "bg-gradient-to-r from-[#5BA946] to-emerald-600 hover:from-[#4a9339] hover:to-emerald-700 text-white font-medium";
      case "compact":
        return "bg-[#5BA946] hover:bg-[#4a9339] text-white text-sm py-1.5 px-3";
      default:
        return "bg-gradient-to-r from-[#5BA946] to-emerald-600 hover:from-[#4a9339] hover:to-emerald-700 text-white font-medium";
    }
  };

  const buttonLabel = variant === "compact" ? t.openAppShort : t.openApp;

  // Render mobile modal content
  const renderMobileModal = () => (
    <>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#5BA946] to-emerald-600 p-5 text-center relative">
        <button
          onClick={closeModal}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white"
          data-testid="close-app-modal"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-white/20 flex items-center justify-center">
          <Smartphone className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-lg font-bold text-white">{t.mobileTitle}</h2>
      </div>

      {/* Content */}
      <div className="p-5">
        <p className="text-gray-600 text-sm text-center mb-5">
          {t.mobileDesc}
        </p>

        {/* Open in App Button */}
        <button
          onClick={handleOpenDeepLink}
          className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-[#5BA946] to-emerald-600 text-white font-medium flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all"
          data-testid="open-in-app-btn"
        >
          <ExternalLink className="w-5 h-5" />
          {t.openInApp}
        </button>

        {/* Get App Section */}
        <div className="mt-5 pt-5 border-t border-gray-100">
          <p className="text-xs text-gray-400 text-center mb-3">
            {t.orGetApp}
          </p>
          <div className="flex gap-2">
            <a
              href={APP_STORE_IOS}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 py-2.5 px-3 rounded-xl bg-black text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-gray-800 active:scale-[0.98] transition-all"
              data-testid="app-store-link"
            >
              <Apple className="w-4 h-4" />
              {t.appStore}
            </a>
            <a
              href={APP_STORE_ANDROID}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 py-2.5 px-3 rounded-xl bg-[#01875f] text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-[#017a56] active:scale-[0.98] transition-all"
              data-testid="play-store-link"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              {t.playStore}
            </a>
          </div>
        </div>
      </div>
    </>
  );

  // Render desktop modal content
  const renderDesktopModal = () => (
    <>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#5BA946] to-emerald-600 p-5 text-center relative">
        <button
          onClick={closeModal}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white"
          data-testid="close-app-modal"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-white/20 flex items-center justify-center">
          <Monitor className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-lg font-bold text-white">{t.desktopTitle}</h2>
      </div>

      {/* Content */}
      <div className="p-5">
        <p className="text-gray-600 text-sm text-center mb-4">
          {t.desktopDesc}
        </p>

        {/* QR Code */}
        {exportData?.deep_link && (
          <div className="mb-4">
            <QRCodeDisplay value={exportData.deep_link} size={180} />
          </div>
        )}

        {/* Copy Link Section */}
        <div className="mb-4">
          <p className="text-xs text-gray-400 text-center mb-2">
            {t.orCopyLink}
          </p>
          <button
            onClick={handleCopyLink}
            className={`w-full py-3 px-4 rounded-xl border-2 font-medium flex items-center justify-center gap-2 transition-all active:scale-[0.98] ${
              copied 
                ? 'border-green-500 bg-green-50 text-green-700' 
                : 'border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="copy-link-btn"
          >
            {copied ? (
              <>
                <Check className="w-5 h-5" />
                {t.copied}
              </>
            ) : (
              <>
                <Copy className="w-5 h-5" />
                {t.copyLink}
              </>
            )}
          </button>
        </div>

        {/* Get App Section */}
        <div className="pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-400 text-center mb-3">
            {t.orGetApp}
          </p>
          <div className="flex gap-2">
            <a
              href={APP_STORE_IOS}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 py-2.5 px-3 rounded-xl bg-black text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-gray-800 active:scale-[0.98] transition-all"
              data-testid="app-store-link"
            >
              <Apple className="w-4 h-4" />
              {t.appStore}
            </a>
            <a
              href={APP_STORE_ANDROID}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 py-2.5 px-3 rounded-xl bg-[#01875f] text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-[#017a56] active:scale-[0.98] transition-all"
              data-testid="play-store-link"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              {t.playStore}
            </a>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Open App Button */}
      <Button
        onClick={handleOpenApp}
        disabled={isLoading}
        className={`gap-2 ${getButtonStyles()} ${className}`}
        data-testid="open-edomatch-btn"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {t.opening}
          </>
        ) : (
          <>
            <Smartphone className="w-4 h-4" />
            {buttonLabel}
          </>
        )}
      </Button>

      {/* Modal - Rendered via Portal */}
      {showModal && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closeModal}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-scaleIn">
            {isMobile ? renderMobileModal() : renderDesktopModal()}
          </div>
          
          {/* Animation styles */}
          <style>{`
            @keyframes scaleIn {
              from { opacity: 0; transform: scale(0.9); }
              to { opacity: 1; transform: scale(1); }
            }
            .animate-scaleIn {
              animation: scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
            }
          `}</style>
        </div>,
        document.body
      )}
    </>
  );
}
